<script setup>
import SvgIcon from '@/components/common/SvgIcon.vue'
import { useLogin } from '@/pages/login/main.js'
import { locationToUrl } from '@utils/utils.js'
import Header from '@/components/common/Header.vue'
import CustomCheckBox from "@components/common/CustomCheckBox.vue";

const {data, account, password, autoComplete, handleLoginEvent, handleForgotEvent} = useLogin()
</script>

<template lang="pug">
  .container
    //- header
    Header(:logo="data?.team?.navLogo")
    .wrap
      .login-container
        .left
          img.login-bg(:src="data?.indexDm", alt="banner", @click="locationToUrl(data?.indexDmUrl)")
        .right
          .login-card
            //- 標題
            h3
              | 立即登入
              br
              | 開始今天的任務！
            //- form
            form(@submit.prevent="handleLoginEvent" :autocomplete="autoComplete ? 'on' : 'off'")
              //- input
              template(v-if="data?.loginOptions?.includes('TEAMS')")
                .password-wrapper#username(:class="{error: account.error.value}" :data-error="account.error.value")
                  svg-icon.icon.account(name="account")
                  input(type="text" name="username" placeholder="請輸入帳號（英數混合）" v-model="account.content.value" autocapitalize="off" autocorrect="off" spellcheck="false" autocomplete="username" required)
                .password-wrapper#password(:class="{error: password.error.value}" :data-error="password.error.value")
                  input(:type="password.isShow.value ? 'text' : 'password'" placeholder="請輸入密碼" v-model="password.content.value" autocapitalize="off" autocorrect="off" spellcheck="false" autocomplete="current-password" required)
                  button(@click="password.toggleShow" type="button")
                    svg-icon.icon.lock(name="lock")
                    svg-icon.icon.eye(:name="password.isShow.value ? 'eye' : 'eye-off'")
                //- forgot
                .forgot-password
                  CustomCheckBox.checkBox(v-model="autoComplete")
                    | 記得我
                  a(href="javascript:void(0)" @click="handleForgotEvent" v-if="data?.forgetPasswordFeature") 忘記密碼
                //- button
                button.standard(@click="handleLoginEvent" type="submit") 登入
                .custom-dashed
              template(v-if="data?.loginOptions?.includes('EDU')")
                button.standard.lite(@click="locationToUrl(data?.eduLoginUrl)")  教育雲登入
    //- footer
    .footer
      p POWERED BY 翰林教育科技
      label Pro
</template>

<style lang="sass" scoped>
@use '@assets/styles/common' as *
@use '@assets/styles/mixin' as *
button.standard.lite
  background-color: unset
// transition fade
.fade-enter-active, .fade-leave-active
  transition: opacity 0.15s

.fade-enter-from, .fade-leave-to
  opacity: 0

.container
  background-color: $webBg-pro

.login-container
  display: flex
  width: 100%
  height: calc(100vh - $headerHeigh - $footerHeigh)
  padding: 0 40px
  @include pad
    padding: 0

  .left
    flex: 7
    padding: 40px 0
    padding-right: 20px
    @include pad
      width: 100%
      height: calc(100vh - $headerHeigh - $footerHeigh)
      position: absolute
      padding: 0

    img.login-bg
      +size(100%)
      object-fit: cover
      border-radius: 16px
      @include pad
        border-radius: unset

  .right
    +flex(center)
    flex: 5
    z-index: 1
    pointer-events: none
    @include sphone
      padding: 0 30px

    .login-card
      width: 400px
      max-width: 90%
      display: flex
      flex-direction: column
      background-color: #F6F7F9
      padding: 40px
      border-radius: 16px
      pointer-events: all
      @include pad
        box-shadow: 0px 0px 40px 0px rgba(25, 25, 25, 0.20)
      @include sphone
        width: 100%
        max-width: 316px
        padding: 40px 20px

      form > *
        margin-bottom: 24px
        @include sphone
          margin-bottom: 16px

      > .lite
        margin-bottom: 0

// loginCard Style
.login-card > h3
  +font(32px, 700)
  color: #0B5EAC
  margin-bottom: 24px
  @include sphone
    +font(24px, 700)
    margin-bottom: 16px

.password-wrapper
  position: relative

  > input
    position: relative
    width: 100%
    padding: 8px
    padding-left: calc(8px + $inputIconSize + 8px)
    padding-right: calc(8px + $inputIconSize + 8px)
    border-radius: 8px
    border: unset

    &::placeholder
      +font(14px, 500)
      line-height: 1.8
      color: #6B6B6B

    &:focus
      outline: unset

  .icon
    +size($inputIconSize)
    position: absolute
    color: #6B6B6B

    &.lock, &.account
      top: 8px
      left: 8px
      z-index: 1

    &.eye
      top: 8px
      right: 8px
      cursor: pointer

.error
  animation-name: shakeX
  animation-duration: 0.4s
  animation-timing-function: cubic-bezier(0.36, 0.07, 0.19, 0.97)
  animation-fill-mode: both
  animation-iteration-count: 1
  // 動畫執行一次
  backface-visibility: hidden
  perspective: 1000px

.password-wrapper.error
  &::after
    content: attr(data-error)
    +font(13px, 500)
    line-height: 1.8
    display: block
    color: #DF4C4C
    text-align: right

  > input
    border: 1px #DF4C4C solid
    color: #DF4C4C

    &::placeholder
      color: #DF4C4C

  .icon
    color: #df4c4c

// 忘記密碼
.forgot-password
  position: relative
  display: flex
  justify-content: space-between

  :deep(.checkBox)
    > label
      +flex(center)
      +font(14px, 500)
      line-height: 1.8
      color: #6B6B6B
      // float: left
      cursor: pointer
      user-select: none

      > input
        +size(20px)
        margin-right: 2px
        cursor: pointer

      .custom-checkbox
        display: none
        +size(20px)
        margin-right: 2px
        background-image: url(@public/checkbox/default.svg)
        background-size: cover

  > a
    +font(14px, 500)
    line-height: 1.8
    color: #0B5EAC
    // float: right
    // 底線設定
    text-decoration-line: underline
    text-decoration-style: solid
    text-decoration-skip-ink: none
    text-decoration-thickness: auto
    text-underline-offset: auto
    text-underline-position: from-font

.custom-dashed
  display: none
  &:not(:last-child)
    display: block
</style>
